import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { ConsumerAuthProvider, useConsumerAuth } from '../features/auth/useConsumerAuth';
// ProtectedRoute reads the raw token to tell "no session at all" (bounce) apart
// from "a session that has not finished confirming" (wait) — see below.
import { getToken, isGuestToken } from '../features/api/consumerClient';
import { AppThemeProvider } from './useAppTheme';
import SiteHeader from '../shared/SiteHeader';
import SiteFooter from '../shared/SiteFooter';
import ScrollToTop from '../shared/ScrollToTop';
import BetaBadge from '../shared/BetaBadge';
import ErrorBoundary from '../shared/ErrorBoundary';
import useDocumentTitle from '../shared/useDocumentTitle';

// ── Code-splitting (2026-09-07) ─────────────────────────────────────────────
// Every page below is lazy-loaded so its JS ships as its OWN chunk, fetched
// only when the route is visited. Before this the whole app — Home, all three
// audience flows, every legal page, AND the entire Borrower OS dashboard (20+
// heavy feature views: Balance Transfer, Debt Freedom, Eligibility, Analysis,
// OffersComparison, DocumentVault…) — was one 573 kB gzipped `main.js` that a
// visitor to /borrower had to download in full before anything painted. The
// dashboard is by far the heaviest and is never needed on the public pages, so
// splitting it out alone cuts the first-paint cost dramatically. The shared
// chrome (header/footer/auth/theme) stays eager — it is small and on every page.
const Login = lazy(() => import('../features/auth/Login'));
// Landing route for a Google sign-in redirect — adopts the returned consumer
// token and moves the borrower into the app. See GoogleCallback.
const GoogleCallback = lazy(() => import('../features/auth/GoogleCallback'));
// Staff opening this borrower's account from View Leads — spends the one-time code. See StaffSession.
const StaffSession = lazy(() => import('../features/auth/StaffSession'));
const VerifyMobile = lazy(() => import('../features/auth/VerifyMobile'));
const Home = lazy(() => import('../features/home/Home'));
const Dashboard = lazy(() => import('../features/dashboard/Dashboard'));
// The three audience pages the top nav points at. Public — they are what someone
// reads BEFORE they have an account, so they sit outside ProtectedRoute.
const BorrowerFlow = lazy(() => import('../features/borrower/borrower-flow'));
const DistributorFlow = lazy(() => import('../features/distributor/distributor-flow'));
const LenderFlow = lazy(() => import('../features/lender/lender-flow'));
// The plans page the header points at after Lender (2026-09-05) — the approved
// pricing prototype, ported in features/pricing.
const PricingPage = lazy(() => import('../features/pricing/PricingPage'));
// Legal/info pages — content pulled from the old interestng.com site so the
// footer's Terms/Privacy/Refund Policy/Contact links have a real destination.
// About Us and Newsroom joined them on 2026-09-01 (who-we-are.html and
// newsroom.html); the footer's 'About Us' had been pointing at '#' until then.
const TermsPage = lazy(() => import('../features/legal/TermsPage'));
const PrivacyPage = lazy(() => import('../features/legal/PrivacyPage'));
const RefundPolicyPage = lazy(() => import('../features/legal/RefundPolicyPage'));
const ContactPage = lazy(() => import('../features/legal/ContactPage'));
const AboutPage = lazy(() => import('../features/legal/AboutPage'));
const NewsroomPage = lazy(() => import('../features/legal/NewsroomPage'));
// Any address no route claims (Testing #30) — see the `*` route below.
const NotFound = lazy(() => import('../shared/NotFound'));

// Names the browser tab for the page it wraps (Testing #25). One wrapper per
// route keeps each title next to its path, and only the mounted page sets one.
// Login and the dashboard name their own tabs: their title changes with the
// screen inside them (Create an account, Active Loans …).
function Titled({ title, children }) {
  useDocumentTitle(title);
  return children;
}

/**
 * The four public pages share ONE header AND one footer instance.
 *
 * They live here rather than inside each page on purpose. Rendered per page, the
 * header is a different element every time you navigate — React unmounts one and
 * mounts another, so it rebuilds on every click even though it looks identical.
 * Here it sits ABOVE the routed content: the same instance stays mounted while
 * only the <Outlet /> beneath it swaps, which is what makes the bar hold still
 * as you move between pages. SiteFooter is the same idea below the content —
 * each of the 4 pages used to render its own (different) inline footer; this
 * replaces all of them with one shared, richer footer (2026-08-29).
 *
 * Login and the dashboard are deliberately outside this layout — they have their
 * own chrome and are not part of the public site.
 */
function PublicLayout() {
  return (
    <>
      {/* Before the header, so a route change lands you at the top of the new
          page. See ScrollToTop — it is scoped to the public site on purpose. */}
      <ScrollToTop />
      <SiteHeader />
      <Outlet />
      <SiteFooter />
    </>
  );
}

// Shown while a lazily-loaded route chunk is being fetched. Deliberately tiny
// and unstyled beyond a centred line — it flashes only on the first visit to a
// route (the chunk is cached after), and matching the app's fuller loading
// states here would itself pull more into the initial bundle.
function RouteFallback() {
  return (
    <div className="flex flex-column align-items-center justify-content-center gap-3 p-6 text-600 text-sm" role="status" aria-live="polite">
      <i className="pi pi-spin pi-spinner pi-2x text-primary" aria-hidden="true" />
      <span>Loading…</span>
    </div>
  );
}

// Resets the error boundary on navigation, so one broken screen never traps the
// borrower — moving anywhere else clears it. Lives inside the router for useLocation.
function RoutedErrorBoundary({ children }) {
  const loc = useLocation();
  return <ErrorBoundary resetKey={loc.pathname}>{children}</ErrorBoundary>;
}

function ProtectedRoute({ children, allowNoMobile = false }) {
  const { isAuthed, loading, consumer } = useConsumerAuth();
  const loc = useLocation();

  // A TOKEN IN HAND MEANS "GIVE THE SESSION A CHANCE TO CONFIRM", NOT "BOUNCE".
  //
  // `loading` only covers the FIRST refresh. Once it has settled it stays false
  // for the life of the page, so any LATER refresh — the one a wizard runs right
  // after a Google sign-in — is in flight while `isAuthed` is still false and
  // `loading` is already false. Landing here in that window rendered
  // <Navigate to="/login"> and threw the borrower out, holding a perfectly valid
  // token, seconds after their offers had been generated and SAVED server-side.
  // That is the "generated 29 offers, then the login page" bug.
  //
  // So when a real (non-guest) token exists we show the same brief Loading… as a
  // cold start instead of redirecting. The auth context resolves within a tick;
  // if the token is genuinely dead, refresh()'s own catch clears it and this
  // falls through to /login on the next render — a moment later, correctly.
  const settling = !isAuthed && !!getToken() && !isGuestToken();
  if (loading || settling) return <RouteFallback />;
  // Carry the page they were actually trying to reach. Login sends a normal
  // sign-in into the conversation, but someone bounced off /dashboard by an
  // expired session should land back on /dashboard — not in a chat they never
  // asked for. `from` is what makes that distinction possible.
  if (!isAuthed) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  // REGISTRATION IS A MUST. A Google sign-in makes an account with no mobile, and
  // the mobile is what a credit report is pulled against — so until one is
  // verified, the only page such an account may open is the one that verifies it.
  // (The server refuses payment for a bureau report without one either way.)
  if (!allowNoMobile && consumer && !consumer.mobile) {
    return <Navigate to="/verify-mobile" replace state={{ from: loc.pathname }} />;
  }
  return children;
}

export default function App() {
  return (
    <AppThemeProvider>
    <ConsumerAuthProvider>
      {/*
        basename comes from PUBLIC_URL, which CRA derives from `homepage` in
        package.json. That is `"/"` as of 2026-08-29 — this app is the site at
        the origin root, so basename resolves to '' and routes are `/borrower`,
        `/lender`, and so on.

        Deriving it rather than hardcoding is what let the app move from `/b2c`
        to the root by changing one line. Keep it derived: if the app is ever
        served under a prefix again, the router must be told separately, or
        `<prefix>/borrower` matches no <Route>, falls through to `*` and
        redirects straight out of the app.
      */}
      <BrowserRouter basename={process.env.PUBLIC_URL} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {/* Suspense boundary for the lazily-loaded route chunks above. It wraps
            <Routes> so the fallback shows for ANY route while its chunk loads,
            public or protected. */}
        <RoutedErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* One header for all four — see PublicLayout above. */}
          <Route element={<PublicLayout />}>
            {/* No title of its own: the home page keeps the site's full name. */}
            <Route path="/" element={<Home />} />
            <Route path="/borrower" element={<Titled title="For Borrowers"><BorrowerFlow /></Titled>} />
            {/* The per-archetype application flow (DistributorApply) opens as a
                big modal ON this page now, not a route of its own — clicking an
                archetype card just sets local state here, no navigation. */}
            <Route path="/distributor" element={<Titled title="For Loan Distributors"><DistributorFlow /></Titled>} />
            <Route path="/lender" element={<Titled title="For Lenders"><LenderFlow /></Titled>} />
            {/* The plans page, ported from the approved pricing prototype. */}
            <Route path="/pricing" element={<Titled title="Pricing"><PricingPage /></Titled>} />
            <Route path="/terms" element={<Titled title="Terms and Conditions"><TermsPage /></Titled>} />
            <Route path="/privacy" element={<Titled title="Privacy Policy"><PrivacyPage /></Titled>} />
            <Route path="/refund-policy" element={<Titled title="Refund & Cancellation Policy"><RefundPolicyPage /></Titled>} />
            <Route path="/contact" element={<Titled title="Contact Us"><ContactPage /></Titled>} />
            <Route path="/about" element={<Titled title="About Us"><AboutPage /></Titled>} />
            <Route path="/newsroom" element={<Titled title="Newsroom"><NewsroomPage /></Titled>} />
            {/* ANY OTHER ADDRESS: "Page not found", inside the site's header and
                footer. It used to <Navigate to="/" replace />, which dropped the
                visitor on Home with no word about why and wiped the address they
                had typed (Testing #30). Paths are basename-relative, so this is
                the same under the /b2c UAT build. */}
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/login" element={<Login />} />
          {/* Google sign-in returns here as a full-page redirect with the
              consumer token in the URL. Outside ProtectedRoute — the borrower
              is not authed yet at this point; the component adopts the token
              then navigates onward. Registered BEFORE the "/login" catch so the
              deeper path wins. */}
          <Route path="/login/google/callback" element={<Titled title="Signing you in"><GoogleCallback /></Titled>} />
          {/* Staff opened this borrower's account from View Leads (code in the #fragment).
              Both paths: the staff app links <b2c base>/impersonate, which is /b2c/impersonate
              on the dev server (served at the root) and /impersonate under a /b2c build. */}
          <Route path="/impersonate" element={<Titled title="Opening the account"><StaffSession /></Titled>} />
          <Route path="/b2c/impersonate" element={<Titled title="Opening the account"><StaffSession /></Titled>} />
          <Route path="/verify-mobile" element={<ProtectedRoute allowNoMobile><Titled title="Verify your mobile"><VerifyMobile /></Titled></ProtectedRoute>} />
          {/* Splat, not a bare `/dashboard`: every Borrower OS section is its own
              URL underneath it (/dashboard/existing-loan-details, …) so a view is
              linkable, bookmarkable and survives refresh + browser Back. Dashboard
              reads the trailing segment itself and normalises anything unknown. */}
          <Route path="/dashboard/*" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        </Routes>
        </Suspense>
        </RoutedErrorBoundary>
        {/* Pre-launch marker. Deliberately OUTSIDE <Routes> so it renders on
            every page — /login and /dashboard/* included, not just the public
            routes PublicLayout wraps. */}
        <BetaBadge />
      </BrowserRouter>
    </ConsumerAuthProvider>
    </AppThemeProvider>
  );
}
